import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Eye, FileCode2, Search } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { CultureBadge } from './CultureBadge'
import { cn } from '@/lib/utils'
import { showErrorToast } from '@/lib/toast'

import { renderCatalogTranslations } from '@/lib/api/v2/catalog-translations'
import type {
  CatalogProductDto,
  CatalogRenderItem,
  CatalogTemplateDto,
  TemplateSyntax,
} from '@/types/catalog-translations'

interface TranslationsTableProps {
  products: CatalogProductDto[]
  cultures: string[]
}

type RowMode = 'rendered' | 'templates'

/**
 * Catalog × cultures matrix. One row per product, one column per supported
 * culture. Default view shows the live rendered preview (sample bag fed
 * through the same renderer the production pipeline uses); click any cell to
 * toggle the row to template view and inspect the raw template syntax.
 *
 * Phase 4 ships consultation only — no inline editing — but the per-row
 * toggle is in place so the future editor can graft onto it without changing
 * the table shape.
 */
export function TranslationsTable({ products, cultures }: TranslationsTableProps) {
  const [renderByProductId, setRenderByProductId] = useState<Record<string, CatalogRenderItem>>({})
  const [renderLoading, setRenderLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [rowModes, setRowModes] = useState<Record<string, RowMode>>({})

  // Trigger one batch render call for the visible products on mount.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (products.length === 0) {
        if (!cancelled) setRenderLoading(false)
        return
      }
      try {
        const resp = await renderCatalogTranslations({ productIds: products.map(p => p.id) })
        if (cancelled) return
        const dict: Record<string, CatalogRenderItem> = {}
        for (const item of resp.items) dict[item.productId] = item
        setRenderByProductId(dict)
      } catch {
        if (!cancelled) showErrorToast('Erreur lors du rendu live des produits.')
      } finally {
        if (!cancelled) setRenderLoading(false)
      }
    }
    void run()
    return () => { cancelled = true }
  }, [products])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return products
    return products.filter(p =>
      p.code.toLowerCase().includes(q) ||
      Object.values(p.labels).some(l => l.toLowerCase().includes(q))
    )
  }, [products, search])

  function toggleMode(productId: string) {
    setRowModes(prev => ({
      ...prev,
      [productId]: prev[productId] === 'templates' ? 'rendered' : 'templates'
    }))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="size-4 text-muted-foreground" />
        <Input
          data-testid="catalog-translations-search-input"
          type="search"
          placeholder="Rechercher un produit (code ou libellé)…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm h-8"
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} produit{filtered.length > 1 ? 's' : ''}
          {filtered.length !== products.length && ` (sur ${products.length})`}
        </span>
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium w-[260px]">Produit</th>
              {cultures.map(c => (
                <th key={c} className="px-3 py-2 font-medium">
                  <div className="flex items-center gap-2">
                    <CultureBadge culture={c} />
                    <span>{c}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={1 + cultures.length} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  Aucun produit ne correspond à votre recherche.
                </td>
              </tr>
            )}
            {filtered.map(product => {
              const renderItem = renderByProductId[product.id]
              const mode: RowMode = rowModes[product.id] ?? 'rendered'
              return (
                <tr key={product.id} data-testid={`catalog-translations-row-${product.code}`} className="align-top">
                  <td className="px-3 py-3 align-top">
                    <ProductCell product={product} mode={mode} onToggle={() => toggleMode(product.id)} />
                  </td>
                  {cultures.map(culture => {
                    const template = product.templates.find(t => t.culture === culture)
                    const cell = renderItem?.renderings.find(r => r.culture === culture)
                    return (
                      <td
                        key={culture}
                        className={cn(
                          'px-3 py-3 align-top cursor-pointer transition-colors',
                          'hover:bg-muted/30',
                          mode === 'templates' && 'bg-muted/20'
                        )}
                        onClick={() => toggleMode(product.id)}
                        data-testid={`catalog-translations-cell-${product.code}-${culture}`}
                      >
                        <CultureCell
                          mode={mode}
                          template={template}
                          renderedName={cell?.name ?? null}
                          renderedDescription={cell?.description ?? null}
                          renderError={cell?.error ?? null}
                          renderOk={cell?.ok ?? null}
                          sampleBagAvailable={renderItem?.sampleBagAvailable ?? false}
                          renderLoading={renderLoading && !renderItem}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Product (left column) ───────────────────────────────────────────────────

interface ProductCellProps {
  product: CatalogProductDto
  mode: RowMode
  onToggle: () => void
}

function ProductCell({ product, mode, onToggle }: ProductCellProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {product.icon && <span aria-hidden className="text-base">{product.icon}</span>}
        <span className="font-medium">{product.labels['fr-BE'] || product.code}</span>
      </div>
      <div className="font-mono text-[11px] text-muted-foreground">{product.code}</div>
      {product.familyGroup && (
        <Badge variant="outline" className="text-[10px] h-4 px-1.5">
          {product.familyGroup}
        </Badge>
      )}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`catalog-translations-toggle-${product.code}`}
        className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        {mode === 'rendered' ? (
          <>
            <FileCode2 className="size-3" />
            <span>Voir templates</span>
          </>
        ) : (
          <>
            <Eye className="size-3" />
            <span>Voir rendu</span>
          </>
        )}
      </button>
    </div>
  )
}

// ── Single (product, culture) cell ──────────────────────────────────────────

interface CultureCellProps {
  mode: RowMode
  template: CatalogTemplateDto | undefined
  renderedName: string | null
  renderedDescription: string | null
  renderError: string | null
  renderOk: boolean | null
  sampleBagAvailable: boolean
  renderLoading: boolean
}

function CultureCell({
  mode,
  template,
  renderedName,
  renderedDescription,
  renderError,
  renderOk,
  sampleBagAvailable,
  renderLoading,
}: CultureCellProps) {
  const missing = !template || !template.present
  const syntaxBadge = template?.syntax ? <SyntaxBadge syntax={template.syntax} /> : null

  if (missing) {
    return (
      <div className="space-y-1 rounded border-l-2 border-destructive/40 bg-destructive/5 px-2 py-1">
        <div className="flex items-center gap-1 text-destructive text-[11px] font-medium">
          <AlertTriangle className="size-3" />
          Template manquant
        </div>
      </div>
    )
  }

  if (mode === 'templates') {
    return (
      <div className="space-y-1.5">
        {syntaxBadge}
        <div className="font-mono text-[11px] leading-snug whitespace-pre-wrap break-words text-foreground/90">
          {template.nameTemplate || <span className="italic text-muted-foreground">(nom vide)</span>}
        </div>
        {template.descriptionTemplate && (
          <div className="font-mono text-[11px] leading-snug whitespace-pre-wrap break-words text-muted-foreground/80 border-t pt-1">
            {template.descriptionTemplate}
          </div>
        )}
      </div>
    )
  }

  // Rendered mode
  if (renderLoading) {
    return <div className="h-8 animate-pulse bg-muted/50 rounded" />
  }
  if (!sampleBagAvailable) {
    return (
      <div className="text-[11px] text-muted-foreground italic flex items-start gap-1">
        <AlertTriangle className="size-3 mt-0.5 shrink-0" />
        <span>Pas de sample bag — preview indisponible.</span>
      </div>
    )
  }
  if (renderOk === false) {
    return (
      <div className="space-y-1 rounded border-l-2 border-destructive/40 bg-destructive/5 px-2 py-1">
        <div className="flex items-center gap-1 text-destructive text-[11px] font-medium">
          <AlertTriangle className="size-3" />
          Erreur de rendu
        </div>
        <div className="text-[11px] text-destructive/80 break-words">{renderError}</div>
        {syntaxBadge}
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 text-[13px] leading-snug font-medium break-words">
          {renderedName || <span className="italic text-muted-foreground">(vide)</span>}
        </div>
        {syntaxBadge}
      </div>
      {renderedDescription && (
        <div className="text-[11px] text-muted-foreground leading-snug break-words">
          {renderedDescription}
        </div>
      )}
    </div>
  )
}

// ── Syntax badge ────────────────────────────────────────────────────────────

function SyntaxBadge({ syntax }: { syntax: TemplateSyntax }) {
  if (syntax === 'simple') {
    return (
      <Badge variant="secondary" className="text-[9px] h-4 px-1 font-mono uppercase">
        simple
      </Badge>
    )
  }
  if (syntax === 'scriban') {
    return (
      <Badge
        variant="outline"
        className="text-[9px] h-4 px-1 font-mono uppercase border-amber-500/50 text-amber-700 dark:text-amber-300"
      >
        scriban
      </Badge>
    )
  }
  return null
}
